/**
 * API Client — wrapper de fetch com:
 *  - Base URL configurável via NEXT_PUBLIC_API_URL
 *  - Injeção automática do JWT access token
 *  - Refresh automático do token em caso de 401
 *  - Sessão encerrada (401 / refresh inválido): limpa tokens e notifica Auth (→ login).
 *    Erros de rede em chamadas soltas não deslogam (evita apagar sessão só porque /auctions falhou).
 */

import { API_BASE_URL } from '../api-base-url';

export { API_BASE_URL };

// ─── Storage keys ──────────────────────────────────────────────────────────
// O refresh token vive apenas num cookie httpOnly definido pelo backend —
// nunca é acessível ao JavaScript. Aqui guardamos só o access token (curta
// duração) e o usuário (cache para render imediato da UI).
const KEYS = {
  accessToken: 'metricaleiloes-access-token',
  user: 'metricaleiloes-user',
};

/** Chave legada do refresh token em localStorage — removida ao escrever/limpar. */
const LEGACY_REFRESH_KEY = 'metricaleiloes-refresh-token';

export const tokenStore = {
  getAccess: () => (typeof window !== 'undefined' ? localStorage.getItem(KEYS.accessToken) : null),
  getUser: () => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem(KEYS.user) ?? 'null');
    } catch {
      return null;
    }
  },
  set: (access: string, user: unknown) => {
    localStorage.setItem(KEYS.accessToken, access);
    localStorage.setItem(KEYS.user, JSON.stringify(user));
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  },
  setAccess: (access: string) => {
    localStorage.setItem(KEYS.accessToken, access);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  },
  setUser: (user: unknown) => {
    localStorage.setItem(KEYS.user, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(KEYS.accessToken);
    localStorage.removeItem(KEYS.user);
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  },
};

// ─── Sessão invalidada (401 / falha ao renovar token) → UI volta ao login ───
const sessionLostListeners = new Set<() => void>();

/** Registrado uma vez no AuthProvider. */
export function subscribeSessionLost(cb: () => void): () => void {
  sessionLostListeners.add(cb);
  return () => sessionLostListeners.delete(cb);
}

function notifySessionLost(): void {
  sessionLostListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* evita quebrar outros listeners */
    }
  });
}

// ─── Tipos ─────────────────────────────────────────────────────────────────
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly message: string,
    public readonly data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
  /** Timeout em ms (default 30s). 0 desliga. */
  timeoutMs?: number;
  /**
   * Retries em erros de rede / 5xx. Aplica só a métodos idempotentes
   * (GET/HEAD/OPTIONS) por padrão — para POST/PATCH/PUT/DELETE,
   * passe explicitamente.
   */
  retries?: number;
}

/** Default global de timeout — sobrescritível por chamada via `timeoutMs`. */
const DEFAULT_TIMEOUT_MS = 30_000;
/** Retries em métodos idempotentes; backoff = 250ms × 2^attempt. */
const DEFAULT_IDEMPOTENT_RETRIES = 2;
const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Timeout dedicado para análise por IA (pipeline multi-agente + download de PDFs)
 * e geo-análise. Default de 30s aborta no meio ("signal is aborted without reason").
 * 6 min cobre downloads lentos + várias etapas OpenAI; alinhar Nginx
 * `proxy_read_timeout` de `/api` a pelo menos este valor.
 */
export const AI_ANALYSIS_TIMEOUT_MS = 360_000;

function isAbortError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  if (err.name === 'AbortError' || err.name === 'TimeoutError') return true;
  return /aborted|timed?\s*out/i.test(err.message);
}

function timeoutErrorMessage(timeoutMs: number): string {
  const sec = Math.round(timeoutMs / 1000);
  return `A análise demorou mais de ${sec}s e foi cancelada. Aguarde e tente de novo — documentos grandes ou vários anexos podem levar alguns minutos.`;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

// ─── Core fetch ────────────────────────────────────────────────────────────
let isRefreshing = false;
/** Resolvers aguardando o refresh em curso; recebem `null` se falhar. */
const refreshQueue: Array<(token: string | null) => void> = [];

async function refreshAccessToken(): Promise<string | null> {
  // Timeout dedicado — refresh tem que ser rápido; se travar, perdemos a sessão
  // localmente em vez de pendurar a UI.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 10_000);
  try {
    // O refresh token vai automaticamente no cookie httpOnly (credentials: include).
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      signal: controller.signal,
    });
    if (!res.ok) {
      tokenStore.clear();
      return null;
    }
    const data = await res.json();
    tokenStore.setAccess(data.accessToken);
    return data.accessToken as string;
  } catch {
    tokenStore.clear();
    return null;
  } finally {
    clearTimeout(timer);
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    body,
    skipAuth = false,
    headers: extraHeaders = {},
    timeoutMs = DEFAULT_TIMEOUT_MS,
    retries,
    method,
    ...rest
  } = options;

  const upperMethod = (method ?? 'GET').toUpperCase();
  const isIdempotent = IDEMPOTENT_METHODS.has(upperMethod);
  // Default: retry só em métodos idempotentes. Caller pode forçar retry em
  // outros métodos passando `retries` explicitamente (cuidado com efeitos colaterais).
  const maxRetries = retries ?? (isIdempotent ? DEFAULT_IDEMPOTENT_RETRIES : 0);

  const buildHeaders = (token?: string | null): HeadersInit => {
    const h: Record<string, string> = {
      ...(extraHeaders as Record<string, string>),
    };
    if (body !== undefined) h['Content-Type'] = 'application/json';
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const makeRequest = async (token?: string | null) => {
    // AbortController por tentativa — cada retry tem timeout independente.
    const controller = timeoutMs > 0 ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => {
          controller.abort(new DOMException(timeoutErrorMessage(timeoutMs), 'TimeoutError'));
        }, timeoutMs)
      : null;
    try {
      return await fetch(`${API_BASE_URL}${path}`, {
        ...rest,
        method,
        // Envia o cookie httpOnly do refresh token nas rotas de auth.
        credentials: 'include',
        headers: buildHeaders(token),
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: controller?.signal,
      });
    } catch (err) {
      if (isAbortError(err)) {
        throw new Error(timeoutErrorMessage(timeoutMs));
      }
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  /** Retry só para falhas transitórias (network / 5xx). 4xx nunca é retry. */
  const requestWithRetry = async (token?: string | null): Promise<Response> => {
    let lastError: unknown = null;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const res = await makeRequest(token);
        if (res.status >= 500 && attempt < maxRetries) {
          await wait(250 * Math.pow(2, attempt));
          continue;
        }
        return res;
      } catch (err) {
        lastError = err;
        // Timeout de análise IA não deve retry (POST longo + custo OpenAI).
        if (isAbortError(err) || (err instanceof Error && /demorou mais de/i.test(err.message))) {
          throw err instanceof Error ? err : new Error(timeoutErrorMessage(timeoutMs));
        }
        // TypeError (rede) e 5xx são transitórios — vale retry idempotente.
        if (attempt < maxRetries) {
          await wait(250 * Math.pow(2, attempt));
          continue;
        }
        throw err;
      }
    }
    // Inalcançável em prática (loop só sai por return/throw), mas mantém o TS feliz.
    throw lastError ?? new Error('apiFetch: retry exhausted');
  };

  const run = async (): Promise<Response> => {
    const accessToken = skipAuth ? null : tokenStore.getAccess();
    let res = await requestWithRetry(accessToken);

    if (res.status === 401 && !skipAuth && accessToken) {
      if (!isRefreshing) {
        isRefreshing = true;
        const newToken = await refreshAccessToken();
        isRefreshing = false;

        const waiters = refreshQueue.splice(0, refreshQueue.length);
        waiters.forEach((resolve) => resolve(newToken));

        if (!newToken) {
          notifySessionLost();
          throw new ApiError(
            401,
            'Sessão expirada ou servidor indisponível. Faça login novamente.',
            null
          );
        }

        res = await requestWithRetry(newToken);
      } else {
        const newToken = await new Promise<string | null>((resolve) => {
          refreshQueue.push(resolve);
        });
        if (!newToken) {
          throw new ApiError(
            401,
            'Sessão expirada ou servidor indisponível. Faça login novamente.',
            null
          );
        }
        res = await requestWithRetry(newToken);
      }
    }

    if (!res.ok) {
      if (res.status === 401 && !skipAuth) {
        tokenStore.clear();
        notifySessionLost();
      }
      let errorData: unknown;
      try {
        errorData = await res.json();
      } catch {
        errorData = null;
      }
      const rawMsg = (errorData as Record<string, string>)?.message ?? `HTTP ${res.status}`;
      const message = Array.isArray(rawMsg) ? rawMsg.join('; ') : (rawMsg as string);
      throw new ApiError(res.status, message, errorData);
    }

    return res;
  };

  const res = await run();

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json() as Promise<T>;
}
